import { useState } from "react";
import StepHeader from "../../components/StepHeader";
import type { ParcelDetails, SelectedContent } from "@shared/types";

export interface LetterSizePreset {
  id: "C6" | "DL" | "C5" | "C4";
  label: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

// Senders choose a familiar envelope name; carriers still receive the
// dimensions their quote APIs require. The 5 mm thickness represents an
// ordinary paper/card envelope, not a parcel-sized padded mailer.
export const LETTER_SIZE_PRESETS: LetterSizePreset[] = [
  { id: "C6", label: "C6 — small card", lengthCm: 16.2, widthCm: 11.4, heightCm: 0.5 },
  { id: "DL", label: "DL — standard letter", lengthCm: 22, widthCm: 11, heightCm: 0.5 },
  { id: "C5", label: "C5 — A5 or folded A4", lengthCm: 22.9, widthCm: 16.2, heightCm: 0.5 },
  { id: "C4", label: "C4 — flat A4 documents", lengthCm: 32.4, widthCm: 22.9, heightCm: 0.5 },
];

interface Props {
  initial: ParcelDetails | null;
  contents: SelectedContent[];
  onSubmit: (parcel: ParcelDetails) => void;
  onBack: () => void;
  submitting: boolean;
}

// Step 4 — parcel details. UK-friendly units, friendly validation, never
// raw NaN/API errors on screen.
function matchesPreset(parcel: ParcelDetails | null, preset: LetterSizePreset): boolean {
  if (!parcel) return false;
  return (
    Math.abs(parcel.lengthCm - preset.lengthCm) < 0.01 &&
    Math.abs(parcel.widthCm - preset.widthCm) < 0.01 &&
    Math.abs(parcel.heightCm - preset.heightCm) < 0.01
  );
}

export function usesLetterForm(contents: SelectedContent[]): boolean {
  return (
    contents.length > 0 &&
    contents.every((content) => content.category === "DOCUMENTS_LETTERS" || content.category === "CARDS")
  );
}

export default function ParcelStep({ initial, contents, onSubmit, onBack, submitting }: Props) {
  const letterForm = usesLetterForm(contents);
  const [weightKg, setWeightKg] = useState(initial ? String(initial.weightGrams / 1000) : "");
  const [weightGramsInput, setWeightGramsInput] = useState(initial ? String(initial.weightGrams) : "");
  const [letterSize, setLetterSize] = useState(
    initial ? (LETTER_SIZE_PRESETS.find((preset) => matchesPreset(initial, preset))?.id ?? "") : "",
  );
  const [length, setLength] = useState(initial ? String(initial.lengthCm) : "");
  const [width, setWidth] = useState(initial ? String(initial.widthCm) : "");
  const [height, setHeight] = useState(initial ? String(initial.heightCm) : "");
  const [value, setValue] = useState(initial ? String(initial.valueGbp) : "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): ParcelDetails | null {
    const nextErrors: Record<string, string> = {};
    const valueGbp = parseFloat(value || "0");

    if (letterForm) {
      const weightGrams = Math.round(parseFloat(weightGramsInput));
      const preset = LETTER_SIZE_PRESETS.find((option) => option.id === letterSize);

      if (!weightGramsInput || Number.isNaN(weightGrams) || weightGrams <= 0) {
        nextErrors.weight = "Enter a weight greater than 0 grams.";
      }
      if (!preset) nextErrors.letterSize = "Choose a letter or envelope size.";
      if (Number.isNaN(valueGbp) || valueGbp < 0) nextErrors.value = "Enter a value of £0 or more.";

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0 || !preset) return null;
      return {
        weightGrams,
        lengthCm: preset.lengthCm,
        widthCm: preset.widthCm,
        heightCm: preset.heightCm,
        valueGbp,
      };
    }

    const weightGrams = Math.round(parseFloat(weightKg) * 1000);
    const lengthCm = parseFloat(length);
    const widthCm = parseFloat(width);
    const heightCm = parseFloat(height);

    if (!weightKg || Number.isNaN(weightGrams) || weightGrams <= 0) nextErrors.weight = "Enter a weight greater than 0.";
    if (!length || Number.isNaN(lengthCm) || lengthCm <= 0) nextErrors.length = "Enter a length greater than 0.";
    if (!width || Number.isNaN(widthCm) || widthCm <= 0) nextErrors.width = "Enter a width greater than 0.";
    if (!height || Number.isNaN(heightCm) || heightCm <= 0) nextErrors.height = "Enter a height greater than 0.";
    if (Number.isNaN(valueGbp) || valueGbp < 0) nextErrors.value = "Enter a value of £0 or more.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return null;
    return { weightGrams, lengthCm, widthCm, heightCm, valueGbp };
  }

  function submit() {
    const parcel = validate();
    if (parcel) onSubmit(parcel);
  }

  return (
    <div className="stack fade-in-up">
      <StepHeader title={letterForm ? "Letter or card details" : "Parcel details"} onBack={onBack} />
      <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
        {letterForm
          ? "Choose the closest envelope size and enter its total weight."
          : "An estimate is fine — weigh it if you can for the most accurate price."}
      </p>

      <div className="card stack">
        {letterForm ? (
          <>
            <div className="field">
              <label htmlFor="weight-grams">Weight (grams)</label>
              <input
                id="weight-grams"
                inputMode="numeric"
                value={weightGramsInput}
                onChange={(event) => setWeightGramsInput(event.target.value)}
                placeholder="e.g. 85"
              />
              {errors.weight && <span className="field-error">{errors.weight}</span>}
            </div>

            <div className="field">
              <label htmlFor="letter-size">Envelope size</label>
              <select id="letter-size" value={letterSize} onChange={(event) => setLetterSize(event.target.value)}>
                <option value="" disabled>
                  Choose a size
                </option>
                {LETTER_SIZE_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
              {errors.letterSize && <span className="field-error">{errors.letterSize}</span>}
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label htmlFor="weight">Weight (kg)</label>
              <input id="weight" inputMode="decimal" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="e.g. 0.5" />
              {errors.weight && <span className="field-error">{errors.weight}</span>}
            </div>

            <div className="stack-sm">
              <label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Dimensions (cm)</label>
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <div className="field">
                  <input aria-label="Length in cm" inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} placeholder="Length" />
                </div>
                <div className="field">
                  <input aria-label="Width in cm" inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="Width" />
                </div>
                <div className="field">
                  <input aria-label="Height in cm" inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="Height" />
                </div>
              </div>
              {(errors.length || errors.width || errors.height) && (
                <span className="field-error">{errors.length || errors.width || errors.height}</span>
              )}
            </div>
          </>
        )}

        <div className="field">
          <label htmlFor="value">Approximate value (£)</label>
          <input id="value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 25" />
          <span className="field-help">Helps us find services with enough compensation cover.</span>
          {errors.value && <span className="field-error">{errors.value}</span>}
        </div>
      </div>

      <button className="btn btn-primary" onClick={submit} disabled={submitting}>
        {submitting ? "Saving…" : "Continue"}
      </button>
    </div>
  );
}
